"use client";

import cn from "@utils/cn";
import React, { FC, useEffect, useRef, useState } from "react";
import SwiperCore from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import { Autoplay, Mousewheel, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import "@/components/HeroCarousel/HeroCarousel.css";
import DynamicImport from "@/components/dynamicImport";
import useIsSaleScreen from "@/hooks/useIsSaleScreen";
import useMediaQuery from "@/hooks/useMediaQuery";

import { HomePageHeroCarouselProps } from "./types";

SwiperCore.use([Mousewheel, Pagination, Autoplay]);

const SalesHomePageHeroCarousel: FC<HomePageHeroCarouselProps> = ({ entry }) => {
  const isSo = useIsSaleScreen();
  let { heroSlides } = entry;
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isMobile = useMediaQuery("(max-width: 600px)");
  const swiperRef = useRef<SwiperCore>();
  const container = useRef<HTMLDivElement>(null);
  const [isAfterVisible, setAfterVisible] = useState(false);
  const [filteredSlides, setFilteredSlides] = useState<any[]>([]);

  const getSiblings = (elm: any) => elm && elm.parentNode && [...elm.parentNode.children].filter((node) => node !== elm);

  useEffect(() => {
    if (container.current && entry.animation) {
      let siblings = getSiblings(container.current);

      siblings.forEach((sibling: HTMLElement) => {
        sibling.style.backgroundColor = "white";
        sibling.style.zIndex = "10";
      });

      if (siblings instanceof HTMLTemplateElement) {
        const templateContent = siblings.content;

        templateContent.querySelectorAll("div").forEach((divInTemplate) => {
          divInTemplate.style.backgroundColor = "white";
          divInTemplate.style.zIndex = "10";
        });
      }
    }
  }, [container.current, entry.animation]);

  // Filter slides based on schedule
  const filterSlidesBySchedule = (slides: any[]) => {
    // Check if any slide has scheduleEnabled = true AND is currently active
    const now = new Date();
    const hasActiveScheduledSlides = slides.some((slide) => {
      if (!slide.scheduleEnabled) return false;
      if (!slide.startDateTime || !slide.endDateTime) return false;

      // Extract time from start and end
      const startTime = new Date(slide.startDateTime);
      const endTime = new Date(slide.endDateTime);

      // Get current time components
      const currentHours = now.getHours();
      const currentMinutes = now.getMinutes();
      const currentTimeInMinutes = currentHours * 60 + currentMinutes;

      // Get start and end time components
      const startHours = startTime.getHours();
      const startMinutes = startTime.getMinutes();
      const startTimeInMinutes = startHours * 60 + startMinutes;

      const endHours = endTime.getHours();
      const endMinutes = endTime.getMinutes();
      const endTimeInMinutes = endHours * 60 + endMinutes;

      return currentTimeInMinutes >= startTimeInMinutes && currentTimeInMinutes <= endTimeInMinutes;
    });

    return slides.filter((slide) => {
      // If there are active scheduled slides, only show slides with scheduleEnabled = true
      if (hasActiveScheduledSlides) {
        // Skip slides without scheduleEnabled
        if (!slide.scheduleEnabled) return false;

        // If scheduleEnabled but no dates, don't show
        if (!slide.startDateTime || !slide.endDateTime) return false;

        // Extract time from start and end
        const startTime = new Date(slide.startDateTime);
        const endTime = new Date(slide.endDateTime);

        // Get current time components
        const currentHours = now.getHours();
        const currentMinutes = now.getMinutes();
        const currentTimeInMinutes = currentHours * 60 + currentMinutes;

        // Get start and end time components
        const startHours = startTime.getHours();
        const startMinutes = startTime.getMinutes();
        const startTimeInMinutes = startHours * 60 + startMinutes;

        const endHours = endTime.getHours();
        const endMinutes = endTime.getMinutes();
        const endTimeInMinutes = endHours * 60 + endMinutes;

        // Show slide only if current time is within the daily time range
        return currentTimeInMinutes >= startTimeInMinutes && currentTimeInMinutes <= endTimeInMinutes;
      }

      // If no active scheduled slides exist, show all slides
      return true;
    });
  };

  // Auto-update filtered slides based on schedule
  useEffect(() => {
    const updateSlides = () => {
      const filtered = filterSlidesBySchedule(heroSlides);
      setFilteredSlides(filtered);
    };

    // Initial filter
    updateSlides();

    // Check every minute for schedule changes
    const interval = setInterval(updateSlides, 60000);

    return () => clearInterval(interval);
  }, [heroSlides]);

  if (!isSo) return null;

  if (filteredSlides.length === 0) return null;

  const singleSlide = filteredSlides.length === 1;

  const handleScroll = () => {
    if (entry.animation) {
      const heroHeight = window.innerHeight;
      const scrollPosition = window.scrollY || window.pageYOffset;

      if (scrollPosition >= heroHeight / (!isMobile || singleSlide ? 1.1 : 1.75)) {
        setAfterVisible(true);
      } else {
        setAfterVisible(false);
      }
    }
  };

  // useEffect(() => {
  //   // At component loading reset scroll to 0
  //   if (entry.animation) {
  //     window.scrollTo({ top: 0, behavior: "instant" });
  //   }
  // }, []);

  // useEffect(() => {
  //   window.addEventListener("scroll", handleScroll);

  //   return () => {
  //     window.removeEventListener("scroll", handleScroll);
  //   };
  // }, []);

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (isDesktop && event.deltaY < 0 && !swiperRef.current?.mousewheel.enabled) {
      swiperRef.current?.mousewheel.enable();
    }
  };

  const getSwiperBreakpoints = (isMultiSlide: boolean) => {
    return {};
  };

  return (
    <div
      className={cn("w-full h-svh", entry.animation && "sticky top-0 left-0 !z-[1] is-sticky", isAfterVisible && "after-visible")}
      id="homepage-hero-carousel"
      ref={container}
    >
      <Swiper
        className="w-full h-full"
        speed={entry?.slideSpeed ? entry.slideSpeed * 1000 : 1500}
        direction="horizontal"
        onSwiper={(swiper) => filteredSlides.length > 1 && (swiperRef.current = swiper)}
        autoplay={
          entry?.autoSlide && filteredSlides.length > 1
            ? {
                delay: 4000,
                disableOnInteraction: false,
                pauseOnMouseEnter: false,
              }
            : false
        }
        keyboard={{
          enabled: true,
          onlyInViewport: true,
        }}
        mousewheel={{
          enabled: true,
          forceToAxis: true,
        }}
        simulateTouch={true}
        pagination={isDesktop ? false : { type: "bullets", clickable: true }}
      >
        {filteredSlides?.map((slide, i) => (
          <SwiperSlide key={i}>
            <DynamicImport components={[slide]} h1ID={"i === 0 && slide.id"} animation={entry.animation} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};
export default SalesHomePageHeroCarousel;
